import { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Group,
  Popover,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconBuildingStore, IconChevronUp } from "@tabler/icons-react";
import { useOwnerScope } from "../context/OwnerScopeContext";

interface ShopVisibilityPickerProps {
  variant?: "sidebar" | "header";
}

export function ShopVisibilityPicker({ variant = "sidebar" }: ShopVisibilityPickerProps = {}) {
  const { allShops, selectedShopIds, setSelectedShopIds, isLoading } = useOwnerScope();
  const [opened, setOpened] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedShopIds), [selectedShopIds]);

  // Even with zero shops we surface a placeholder so the user sees the
  // picker exists. Previously returning null left the header looking like
  // the picker was missing, when in fact the scope fetch had returned 0
  // shops (403, missing role, empty result, etc).
  if (!allShops.length) {
    const isHeader = variant === "header";
    const placeholder = isLoading ? "Loading shops…" : "No shops available";
    if (isHeader) {
      return (
        <Box
          px={10}
          py={6}
          style={{
            borderRadius: 8,
            border: "1px solid var(--mantine-color-gray-3)",
            background: "white",
            color: "var(--mantine-color-gray-6)",
          }}
        >
          <Group gap={8} wrap="nowrap">
            <IconBuildingStore size={14} stroke={1.6} />
            <Text size="13px" fw={500} lh={1}>{placeholder}</Text>
          </Group>
        </Box>
      );
    }
    return (
      <Box px="sm" py={8}>
        <Group gap="xs" wrap="nowrap">
          <IconBuildingStore size={16} stroke={1.5} />
          <Box>
            <Text size="10px" c="dimmed" tt="uppercase" lh={1} style={{ letterSpacing: "0.06em" }}>
              Viewing
            </Text>
            <Text size="sm" c="dimmed" lh={1.2}>{placeholder}</Text>
          </Box>
        </Group>
      </Box>
    );
  }

  const allSelected = selectedShopIds.length === allShops.length;
  const labelText =
    allSelected
      ? `All shops · ${allShops.length}`
      : selectedShopIds.length === 0
        ? "No shop"
        : selectedShopIds.length === 1
          ? allShops.find((s) => s.id === selectedShopIds[0])?.name || "1 shop"
          : `${selectedShopIds.length} of ${allShops.length} shops`;

  const toggle = (id: number) => {
    if (selectedSet.has(id)) {
      setSelectedShopIds(selectedShopIds.filter((x) => x !== id));
    } else {
      setSelectedShopIds([...selectedShopIds, id]);
    }
  };

  const isHeader = variant === "header";

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position={isHeader ? "bottom-end" : "top-start"}
      shadow="md"
      width={260}
      withArrow
    >
      <Popover.Target>
        {isHeader ? (
          <UnstyledButton
            onClick={() => setOpened((v) => !v)}
            px={10}
            py={6}
            style={{
              borderRadius: 8,
              border: "1px solid var(--mantine-color-gray-3)",
              background: opened ? "rgba(67,56,202,0.06)" : "white",
              color: "var(--mantine-color-dark-7)",
              transition: "background 120ms ease, border-color 120ms ease",
            }}
            onMouseEnter={(e) => { if (!opened) e.currentTarget.style.background = "rgba(0,0,0,0.04)"; }}
            onMouseLeave={(e) => { if (!opened) e.currentTarget.style.background = "white"; }}
          >
            <Group gap={8} wrap="nowrap">
              <IconBuildingStore size={14} stroke={1.6} />
              <Text size="13px" fw={500} lh={1}>{labelText}</Text>
              <IconChevronUp
                size={12}
                stroke={1.75}
                style={{ transform: opened ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 150ms" }}
              />
            </Group>
          </UnstyledButton>
        ) : (
          <UnstyledButton
            onClick={() => setOpened((v) => !v)}
            w="100%"
            px="sm"
            py={8}
            style={{
              borderRadius: "var(--mantine-radius-md)",
              color: "rgba(0,0,0,0.65)",
              transition: "background-color 150ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                <IconBuildingStore size={16} stroke={1.5} />
                <Box>
                  <Text size="10px" c="dimmed" tt="uppercase" lh={1} style={{ letterSpacing: "0.06em" }}>
                    Viewing
                  </Text>
                  <Text size="sm" lh={1.2}>{labelText}</Text>
                </Box>
              </Group>
              <IconChevronUp
                size={14}
                stroke={1.5}
                style={{ transform: opened ? "rotate(180deg)" : "rotate(0)", transition: "transform 150ms" }}
              />
            </Group>
          </UnstyledButton>
        )}
      </Popover.Target>
      <Popover.Dropdown p={8}>
        <Group justify="space-between" align="center" mb={6}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">Shops</Text>
          <UnstyledButton
            onClick={() => setSelectedShopIds(allSelected ? [] : allShops.map((s) => s.id))}
          >
            <Text size="xs" c="#4F46E5">{allSelected ? "Clear" : "All"}</Text>
          </UnstyledButton>
        </Group>
        <ScrollArea h={Math.min(allShops.length * 32 + 4, 240)}>
          <Stack gap={2}>
            {allShops.map((s) => (
              <UnstyledButton
                key={s.id}
                onClick={() => toggle(s.id)}
                px={8}
                py={6}
                style={{ borderRadius: 6 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <Group gap="xs" wrap="nowrap">
                  <Checkbox
                    checked={selectedSet.has(s.id)}
                    onChange={() => toggle(s.id)}
                    size="xs"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={s.name}
                  />
                  <Text size="sm" style={{ flex: 1 }}>{s.name}</Text>
                </Group>
              </UnstyledButton>
            ))}
          </Stack>
        </ScrollArea>
      </Popover.Dropdown>
    </Popover>
  );
}
